import React from 'react';
import ReactDOM from 'react-dom';
import { HelpCircle, BookOpen } from 'lucide-react';
import { cn } from '../../utils/cn';
import { HelpEntry } from '../../utils/strategy/strategyHelpText';
import { isWhiteLabel } from '../../config/tenantConfig';

const wlBrand = {
  iconText: isWhiteLabel() ? 'text-zebu-500' : 'text-blue-500',
  hoverIcon: isWhiteLabel() ? 'hover:text-zebu-500' : 'hover:text-blue-500',
  focusRing: isWhiteLabel() ? 'focus:ring-zebu-500' : 'focus:ring-blue-500',
  docLink: isWhiteLabel()
    ? 'bg-zebu-50 dark:bg-zebu-900/30 hover:bg-zebu-100 dark:hover:bg-zebu-900/50 text-zebu-700 dark:text-zebu-400'
    : 'bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-400',
};

type HelpTipProps = {
  entry: HelpEntry;
  className?: string;
  placement?: 'bottom' | 'side';
  stopPropagation?: boolean;
};

const CARD_WIDTH = 288; // w-72 = 18rem = 288px
const GAP = 8;

export default function HelpTip({
  entry,
  className,
  placement = 'bottom',
  stopPropagation = true,
}: HelpTipProps) {
  const [open, setOpen] = React.useState(false);
  const [cardStyle, setCardStyle] = React.useState<React.CSSProperties>({});
  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const instanceId = React.useRef(Math.random().toString(36).slice(2));

  // Close this tooltip when another HelpTip opens
  React.useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail !== instanceId.current) setOpen(false);
    };
    document.addEventListener('helptip:open', handler);
    return () => document.removeEventListener('helptip:open', handler);
  }, []);

  // Compute portal position from the button's viewport rect
  const reposition = React.useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top: number;
    let left: number;

    if (placement === 'side') {
      // Try right of icon first; flip left if overflows viewport
      const rightEdge = r.right + GAP + CARD_WIDTH;
      if (rightEdge > vw - GAP) {
        left = r.left - GAP - CARD_WIDTH;
      } else {
        left = r.right + GAP;
      }
      top = r.top + r.height / 2; // will be adjusted with translateY(-50%)
    } else {
      // placement === 'bottom': centre-align under the icon, clamp horizontally
      left = r.left + r.width / 2 - CARD_WIDTH / 2;
      left = Math.max(GAP, Math.min(left, vw - CARD_WIDTH - GAP));
      top = r.bottom + GAP;
      // If near bottom of viewport, flip above
      const card = cardRef.current;
      const cardHeight = card ? card.offsetHeight : 160;
      if (top + cardHeight > vh - GAP) {
        top = r.top - GAP - cardHeight;
      }
    }

    setCardStyle({
      position: 'fixed',
      top,
      left,
      width: CARD_WIDTH,
      zIndex: 9999,
      transform: placement === 'side' ? 'translateY(-50%)' : undefined,
    });
  }, [placement]);

  React.useEffect(() => {
    if (!open) return;
    // Two-pass: first render off-screen to measure height, then reposition
    reposition();
    const raf = window.requestAnimationFrame(() => reposition());
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target as Node) &&
        cardRef.current && !cardRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const card = open ? ReactDOM.createPortal(
    <div
      ref={cardRef}
      role="dialog"
      aria-label={entry.label}
      style={{ ...cardStyle, animation: 'helptip-in 150ms ease-out both' }}
    >
      <style>{`
        @keyframes helptip-in {
          from { opacity: 0; transform: ${placement === 'side' ? 'translateY(-50%) translateX(-4px)' : 'translateY(4px)'}; }
          to   { opacity: 1; transform: ${placement === 'side' ? 'translateY(-50%) translateX(0)' : 'translateY(0)'}; }
        }
      `}</style>
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">
          <HelpCircle className={`h-3.5 w-3.5 ${wlBrand.iconText} flex-shrink-0`} />
          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            {entry.label}
          </span>
        </div>

        {/* Body */}
        <div className="px-3 py-2.5 space-y-2">
          <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
            {entry.text}
          </p>

          {/* Quick example */}
          {entry.example && (
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 px-2.5 py-2 text-xs text-gray-700 dark:text-gray-300 leading-snug">
              <span className="block text-gray-400 dark:text-gray-500 text-[10px] font-semibold uppercase tracking-wide mb-1">
                Example
              </span>
              {entry.example}
            </div>
          )}

          {/* Full guide link */}
          {entry.docsUrl && (
            <div className="flex justify-end pt-0.5">
              <a
                href={entry.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className={`inline-flex items-center gap-1 rounded-lg ${wlBrand.docLink} px-2.5 py-1.5 text-xs font-medium transition-colors`}
              >
                <BookOpen className="h-3 w-3" />
                Full Guide
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <span className={cn('relative inline-flex items-center', className)}>
      <button
        ref={btnRef}
        type="button"
        aria-label={`Help: ${entry.label}`}
        aria-expanded={open}
        onMouseDown={(e) => { if (stopPropagation) e.stopPropagation(); }}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          setOpen((v) => {
            if (!v) document.dispatchEvent(new CustomEvent('helptip:open', { detail: instanceId.current }));
            return !v;
          });
        }}
        className={cn(
          'inline-flex items-center justify-center rounded-full p-0.5 transition-all duration-150',
          `focus:outline-none focus:ring-2 ${wlBrand.focusRing}`,
          open
            ? `${wlBrand.iconText} scale-110`
            : `text-gray-400 ${wlBrand.hoverIcon} hover:scale-110`,
        )}
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {card}
    </span>
  );
}
